// src/pages/Products.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../supabase/axios";
import ProductCard from "../components/ProductCard";
import Layout from "../layout/Layout";
import { toast } from "react-toastify";
import { FiFilter, FiChevronDown, FiX, FiSearch } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { useCurrency } from "../supabase/CurrencyContext";
import { useLoading } from "../context/LoadingContext";

const ITEMS_PER_PAGE = 30;
const FALLBACK_CATEGORIES = ["Men", "Women", "Accessories"];

const asBool = (v) => v === true || v === "true" || v === 1 || v === "1";
const categoryName = (p) =>
  p?.categories?.name ||
  (p?.category && p.category.name) ||
  (Array.isArray(p?.categories) ? p.categories[0]?.Name : null) ||
  "Accessories";

const IMAGE_BASE =
  process.env.REACT_APP_IMAGE_BASE ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

const normalizeImageUrl = (u) => {
  if (!u || typeof u !== "string") return "";
  if (/^(data:|blob:|https?:\/\/)/i.test(u)) return u;
  if (u.startsWith("/")) return `${IMAGE_BASE}${u}`;
  return `${IMAGE_BASE}/${u}`;
};

const pickImageUrl = (img) =>
  normalizeImageUrl(
    img?.image_url ||
      img?.url ||
      img?.publicUrl ||
      img?.public_url ||
      img?.Location ||
      img?.location ||
      img?.path ||
      ""
  );

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const Products = () => {
  const {
    currency = "USD",
    loading: currencyLoading = true,
    convertFromINR,
    baseCurrency = "INR",
  } = useCurrency() || {};

  const [allProducts, setAllProducts] = useState([]);
  const { setLoading } = useLoading();
  const [loading, setLocalLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 400);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const menuBtnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const category = searchParams.get("name");
    if (category) setSelectedCategories(new Set([category]));
  }, [searchParams]);

  useEffect(() => {
    const query = searchParams.get("search") || "";
    setSearch(query);
  }, [searchParams]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setLocalLoading(true);
      const minDelay = new Promise((resolve) => setTimeout(resolve, 600));
      const category =
        searchParams.get("name") || Array.from(selectedCategories)[0] || "";
      const response = await api.get(`/api/products`, {
        params: {
          category: category || undefined,
          search: debouncedSearch || undefined,
        },
      });
      await minDelay;
      const products = response.data || [];
      const mapped = products.map((p) => {
        const imgs = Array.isArray(p.product_images) ? p.product_images : [];
        const ordered = [
          ...imgs.filter((i) => asBool(i?.is_hero)).map(pickImageUrl),
          ...imgs.filter((i) => !asBool(i?.is_hero)).map(pickImageUrl),
        ].filter(Boolean);
        const basePriceINR = Number(p.Price || 0);
        return {
          id: p.ProductId,
          name: p.Name,
          description: p.Description || "No description available",
          priceINR: basePriceINR,
          price: basePriceINR,
          currency: p.currency || baseCurrency,
          category: categoryName(p),
          image: ordered[0] || "/assets/images/placeholder.png",
          baseCurrency,
        };
      });
      setAllProducts(mapped);
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.error || "An error occurred.");
      setAllProducts([]);
    } finally {
      setLoading(false);
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    if (!currencyLoading) fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, currencyLoading, debouncedSearch, selectedCategories]);

  const categoryOptions = useMemo(() => {
    const set = new Set(allProducts.map((p) => p.category).filter(Boolean));
    if (set.size === 0) return FALLBACK_CATEGORIES;
    return Array.from(set);
  }, [allProducts]);

  const filtered = useMemo(() => {
    let data = [...allProducts];
    if (selectedCategories.size > 0)
      data = data.filter((p) => selectedCategories.has(p.category));
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      data = data.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          String(p.price).toLowerCase().includes(q)
      );
    }
    if (sort === "price-asc") data.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") data.sort((a, b) => b.price - a.price);
    return data;
  }, [allProducts, selectedCategories, debouncedSearch, sort]);

  useEffect(() => { setPage(1); }, [selectedCategories, debouncedSearch, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const start = (page - 1) * ITEMS_PER_PAGE;
  const paged = filtered.slice(start, start + ITEMS_PER_PAGE);

  const pagedWithDisplayPrice = useMemo(
    () =>
      paged.map((p) => ({
        ...p,
        displayPrice: convertFromINR
          ? convertFromINR(p.priceINR ?? p.price)
          : p.price,
        displayCurrency: currency,
      })),
    [paged, convertFromINR, currency]
  );

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      if (next.size > 0)
        setSearchParams({ name: Array.from(next)[0], search: debouncedSearch || "" });
      else
        setSearchParams(debouncedSearch ? { search: debouncedSearch } : {});
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedCategories(new Set());
    setSearch("");
    setSort("relevance");
    setSearchParams({});
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuOpen || isMobile) return;
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        menuBtnRef.current && !menuBtnRef.current.contains(e.target)
      ) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, isMobile]);

  const activeCount =
    (search.trim() ? 1 : 0) +
    (selectedCategories.size > 0 ? 1 : 0) +
    (sort !== "relevance" ? 1 : 0);

  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.shahumumbai.com";
  const canonical = `${baseUrl}/products`;
  const pageTitle =
    selectedCategories.size > 0
      ? `${Array.from(selectedCategories)[0]} Products — Shahu Mumbai`
      : "Discover Handpicked Styles — Shahu Mumbai";
  const pageDesc =
    selectedCategories.size > 0
      ? `Explore ${Array.from(selectedCategories)[0]} products at Shahu Mumbai.`
      : "Explore curated fashion in earthy tones and timeless silhouettes at Shahu Mumbai.";

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: selectedCategories.size > 0 ? `${Array.from(selectedCategories)[0]} Products` : "Products",
    url: `${canonical}${debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : ""}`,
    numberOfItems: pagedWithDisplayPrice.length,
    itemListElement: pagedWithDisplayPrice.map((p, idx) => ({
      "@type": "ListItem",
      position: start + idx + 1,
      item: {
        "@type": "Product",
        name: p.name,
        url: `${baseUrl}/products/${p.id}`,
        image: p.image ? [p.image] : undefined,
        description: p.description,
        category: p.category,
        offers: {
          "@type": "Offer",
          url: `${baseUrl}/products/${p.id}`,
          priceCurrency: p.displayCurrency || currency,
          price: typeof p.displayPrice === "number" ? p.displayPrice.toFixed(2) : String(p.displayPrice),
          availability: "https://schema.org/InStock",
        },
      },
    })),
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1, y: 0, scale: 1,
      transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <Layout>
          <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDesc} />
            <meta name="robots" content={pagedWithDisplayPrice.length === 0 ? "noindex,follow" : "index,follow,max-image-preview:large"} />
            <meta name="keywords" content={["Shahu Mumbai","handwoven sarees","artisan-made","sustainable luxury","Indian fashion","designer sarees",...Array.from(selectedCategories||[])].filter(Boolean).join(", ")} />
            <link rel="canonical" href={canonical} />
            <link rel="alternate" hrefLang="en-IN" href={canonical} />
            <link rel="alternate" hrefLang="x-default" href={canonical} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Shahu Mumbai" />
            <meta property="og:locale" content="en_IN" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDesc} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={`${baseUrl}/og/products.jpg`} />
            <meta property="og:image:alt" content="Shahu Mumbai — product collection" />
            <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"BreadcrumbList",itemListElement:[{"@type":"ListItem",position:1,name:"Home",item:`${baseUrl}/`},{"@type":"ListItem",position:2,name:"Products",item:canonical}]})}</script>
            <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":debouncedSearch?"SearchResultsPage":"CollectionPage",name:pageTitle,url:canonical,description:pageDesc,isPartOf:{"@type":"WebSite",name:"Shahu Mumbai",url:baseUrl},...(debouncedSearch?{query:debouncedSearch}:{})})}</script>
            <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
          </Helmet>
    
          <div className="pt-[10px] pb-12 px-2 xs:px-4 bg-[#EDE1DF] min-h-screen font-serif products-container">
    
            {/* ── FILTER BAR (was the banner area) ── */}
            <div className="max-w-7xl mx-auto mt-4 xs:mt-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
    
                {/* Count */}
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#8A6A53]">
                  {filtered.length === 0 ? 0 : start + 1}–{Math.min(filtered.length, start + ITEMS_PER_PAGE)}{" "}
                  of {filtered.length} pieces
                </p>

            {/* Filter & Sort button */}
            <div className="relative w-full sm:w-auto">
              <button
                ref={menuBtnRef}
                onClick={() => setMenuOpen((o) => !o)}
                className="relative flex items-center gap-2 px-3 xs:px-4 py-2 border border-[#D6C3B4] bg-[#F5EDE9] hover:bg-[#EDE1DF] w-full sm:w-auto justify-center sm:justify-start transition-colors duration-200"
              >
                <FiFilter size={14} className="text-[#8A6A53]" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#3E2C23]">Filter & Sort</span>
                <FiChevronDown
                  size={12}
                  className={`text-[#8A6A53] transition-transform duration-300 ${menuOpen ? "rotate-180" : ""}`}
                />
                {activeCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 text-[9px] leading-none bg-[#3E2C23] text-white px-1.5 py-0.5">
                    {activeCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    ref={menuRef}
                    initial={{ opacity: 0, y: isMobile ? 20 : -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: isMobile ? 20 : -8 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className={`${
                      isMobile
                        ? "static w-full mt-3 border border-[#D6C3B4] bg-[#F5EDE9]"
                        : "absolute right-0 mt-2 w-[300px] z-20 border border-[#D6C3B4] bg-[#F5EDE9]"
                    }`}
                  >
                    <div className="p-5 space-y-6">

                      {/* Search */}
                      <div>
                        <label className="block text-[9px] uppercase tracking-[0.35em] text-[#8A6A53] mb-3">
                          Search
                        </label>
                        <div className="relative">
                          <FiSearch className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A6A53]" size={12} />
                          <input
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setSearchParams(
                                e.target.value.trim() || selectedCategories.size > 0
                                  ? { name: Array.from(selectedCategories)[0] || "", search: e.target.value.trim() }
                                  : {}
                              );
                            }}
                            placeholder="Search products…"
                            className="w-full pl-5 pb-2 border-b border-[#D6C3B4] bg-transparent text-[13px] text-[#3E2C23] focus:outline-none focus:border-[#6B4226] placeholder-[#C4A89A]"
                          />
                        </div>
                      </div>

                      {/* Categories */}
                      <div>
                        <div className="flex items-baseline justify-between mb-3">
                          <label className="text-[9px] uppercase tracking-[0.35em] text-[#8A6A53]">
                            Category
                          </label>
                          {selectedCategories.size > 0 && (
                            <button
                              onClick={() => setSelectedCategories(new Set())}
                              className="text-[9px] uppercase tracking-[0.2em] text-[#8A6A53] underline underline-offset-2 hover:text-[#3E2C23]"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          {categoryOptions.map((cat) => {
                            const active = selectedCategories.has(cat);
                            return (
                              <button
                                key={cat}
                                onClick={() => toggleCategory(cat)}
                                className={`text-left text-[12px] tracking-wide py-1 border-b transition-colors duration-150 ${
                                  active
                                    ? "border-[#3E2C23] text-[#1C1008]"
                                    : "border-transparent text-[#8A6A53] hover:text-[#3E2C23]"
                                }`}
                              >
                                {active ? "— " : ""}{cat}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Sort */}
                      <div>
                        <label className="block text-[9px] uppercase tracking-[0.35em] text-[#8A6A53] mb-3">
                          Sort by
                        </label>
                        <div className="flex flex-col gap-2">
                          {[
                            { v: "relevance", label: "Featured" },
                            { v: "price-asc", label: "Price — Low to High" },
                            { v: "price-desc", label: "Price — High to Low" },
                          ].map((opt) => (
                            <button
                              key={opt.v}
                              onClick={() => setSort(opt.v)}
                              className={`text-left text-[12px] tracking-wide py-1 border-b transition-colors duration-150 ${
                                sort === opt.v
                                  ? "border-[#3E2C23] text-[#1C1008]"
                                  : "border-transparent text-[#8A6A53] hover:text-[#3E2C23]"
                              }`}
                            >
                              {sort === opt.v ? "— " : ""}{opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-5 py-4 border-t border-[#D6C3B4]">
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-1 text-[9px] uppercase tracking-[0.3em] text-[#8A6A53] underline underline-offset-2 hover:text-[#3E2C23]"
                      >
                        <FiX size={11} /> Reset
                      </button>
                      <button
                        onClick={() => setMenuOpen(false)}
                        className="bg-[#1C1008] text-white px-5 py-2 text-[9px] uppercase tracking-[0.3em] hover:bg-[#3E2C23] transition-colors duration-200"
                      >
                        Apply
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* ── PRODUCT GRID ── */}
        <div className="max-w-7xl mx-auto mt-4 xs:mt-6 products-grid">
          <div className="grid gap-4 xs:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {loading || currencyLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-80 bg-[#E4D7D3] animate-pulse w-full max-w-[300px] mx-auto"
                />
              ))
            ) : pagedWithDisplayPrice.length > 0 ? (
              pagedWithDisplayPrice.map((product, index) => (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.05 }}
                  className="w-full"
                >
                  <ProductCard product={product} currency={currency} />
                </motion.div>
              ))
            ) : (
              <p className="text-center col-span-full text-[11px] uppercase tracking-[0.4em] text-[#8A6A53] py-16">
                No products found
              </p>
            )}
          </div>

          {/* Pagination */}
          <AnimatePresence mode="wait">
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-wrap items-center justify-center gap-1 xs:gap-2 mt-6 xs:mt-8"
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#8A6A53] hover:text-[#3E2C23] disabled:opacity-30 transition-colors duration-200"
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const n = idx + 1;
                  return (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`w-7 h-7 text-[11px] transition-colors duration-150 ${
                        n === page
                          ? "bg-[#1C1008] text-white"
                          : "text-[#8A6A53] hover:text-[#3E2C23]"
                      }`}
                    >
                      {n}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#8A6A53] hover:text-[#3E2C23] disabled:opacity-30 transition-colors duration-200"
                >
                  Next →
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </Layout>
  );
};

export default Products;
